import { useContext, useEffect, useState } from 'react'
import { TextInput } from '@/components/TextInput/TextInput'
import { Button } from '@/components/Button/Button'
import { LoadingSpinner } from '@/components/LoadingSpinner/LoadingSpinner'
import {
  Page,
  Container,
  LogoContainer,
  ContentContainer,
  Title,
  Paragraph,
  Form,
  Label,
  Link,
  ButtonsContainer,
} from '@/styles/page.styles'
import svgs from '@/constants/svgs'

import { StoreContext } from '@/components/App'
import { observer } from 'mobx-react'
import axios from 'axios'
import { API_URL } from '@/constants/envs'

export const ResetPasswordPage = observer(() => {
  const store = useContext(StoreContext)
  const { theme, themeId, providerUrl } = store.AppState

  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState(false)
  const [emailErrorMessage, setEmailErrorMessage] = useState('')
  const [redirecting, setRedirecting] = useState(false)

  const validateEmail = (email: string): RegExpMatchArray => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      )
  }

  useEffect(() => {
    if (email) {
      const validEmail = validateEmail(email)
      setEmailError(!validEmail)
      setEmailErrorMessage(!validEmail ? 'Wrong email format' : '')
    } else {
      setEmailError(false)
      setEmailErrorMessage('')
    }
  }, [email])

  const handleResetPassword = async () => {
    try {
      const res = await axios.post(
        `${API_URL}/${providerUrl}/password-reset`,
        {
          username: email,
        },
        {
          withCredentials: true,
        },
      )

      setRedirecting(true)
      if (res.data?.proceedToken) {
        window.location.href = res.data.proceedToken
      }
    } catch (e) {
      setRedirecting(false)
      setEmailError(true)
      setEmailErrorMessage(
        'We cannot initiate identity verification process for that email address. Please contact your domain administrator: john.smith@example.com',
      )
      console.log(e)
    }
  }

  return (
    <Page {...theme.app}>
      <Container {...theme.container}>
        {theme.resetPasswordPage.$showAboveTitle && (
          <LogoContainer>
            {theme.logo.$svg && (
              <div>
                <img src={theme.logo.$svg} />
              </div>
            )}
            {svgs[themeId].resetPassword && (
              <div className={redirecting && 'empty'}>
                <img src={svgs[themeId].resetPassword} />
              </div>
            )}
          </LogoContainer>
        )}

        <ContentContainer {...theme.container}>
          {!redirecting ? (
            <>
              <Title {...theme.title}>Reset your password</Title>
              {!theme.resetPasswordPage.$showAboveTitle && (
                <LogoContainer>
                  <img src={svgs[themeId].resetPassword} />
                </LogoContainer>
              )}
              <Paragraph {...theme.paragraph}>
                Please provide your email address. To reset your password we will ask you to verify yourself via{' '}
                <Link {...theme.link}>BankID</Link>. Click continue to proceeed.
              </Paragraph>
              <Form>
                <Label {...theme.label}>Email address</Label>
                <TextInput
                  type="email"
                  className="error"
                  onChange={(e) => setEmail(e.target.value)}
                  error={emailError}
                  errorMessage={emailErrorMessage}
                ></TextInput>
              </Form>
              <ButtonsContainer {...theme.buttonsContainer}>
                <Link {...theme.link}>Back</Link>
                <Button {...theme.button} isDisabled={!email || emailError} onClick={handleResetPassword}>
                  Continue
                </Button>
              </ButtonsContainer>
            </>
          ) : (
            <>
              <Title {...theme.title}>Redirecting to BankID</Title>
              <LoadingSpinner />
              <Paragraph {...theme.paragraph}>Please wait...</Paragraph>
            </>
          )}
        </ContentContainer>
      </Container>
    </Page>
  )
})
